import React, { useEffect, useState, useCallback } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  getTrackedOrderDisplayStatus,
  getTrackedOrders,
  refreshTrackedOrderStatuses,
} from "../utils/lastOrder";
import Images from "../utils/Images";

const formatPlacedAt = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const OrdersList = () => {
  const navigate = useNavigate();
  const { outletId: outletIdFromPath } = useParams();
  const outletId = outletIdFromPath || localStorage.getItem("outletId");
  const [orders, setOrders] = useState(() => getTrackedOrders(outletId));

  useEffect(() => {
    let cancelled = false;

    const syncOrders = async () => {
      const fresh = await refreshTrackedOrderStatuses(outletId);
      if (!cancelled) {
        setOrders(fresh);
      }
    };

    setOrders(getTrackedOrders(outletId));
    syncOrders();

    const intervalId = window.setInterval(syncOrders, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [outletId]);

  const goToMenu = () => {
    if (outletId) {
      navigate(`/${outletId}`, { replace: true });
      return;
    }

    navigate(-1);
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        background:
          "linear-gradient(180deg, #DDEFF6 0px, #F4FBFE 140px, #F8FDFF 100%)",
        pb: "max(24px, env(safe-area-inset-bottom))",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minHeight: 56,
          px: 1.25,
          py: 1,
          backgroundColor: "#09A5F7",
          borderBottomLeftRadius: "18px",
          borderBottomRightRadius: "18px",
        }}
      >
        <IconButton
          onClick={goToMenu}
          aria-label="Back to menu"
          sx={{ color: "#FFFFFF" }}
        >
          <ArrowBackRoundedIcon />
        </IconButton>
        <Typography sx={{ color: "#FFFFFF", fontSize: 18, fontWeight: 800 }}>
          Your orders
        </Typography>
      </Box>

      <Box sx={{ px: 2, pt: 2, maxWidth: 430, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: {
              xs: 1,
              sm: 1.5,
            },
            pt: 2.5,
            pb: 2.5,
          }}
        >
          <Box
            sx={{
              width: "48%",
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={Images.orderBird}
              alt=""
              sx={{
                width: "100%",
                maxWidth: 180,
                height: 130,
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                color: "#003049",
                fontSize: {
                  xs: 18,
                  sm: 20,
                },
                fontWeight: 900,
                lineHeight: 1.2,
              }}
            >
              Recent orders
            </Typography>

            <Typography
              sx={{
                mt: 0.75,
                color: "#587483",
                fontSize: {
                  xs: 12.5,
                  sm: 13,
                },
                lineHeight: 1.55,
              }}
            >
              New orders stay here until they are completed.
            </Typography>
          </Box>
        </Box>
        {orders.length === 0 ? (
          <Box sx={{ textAlign: "center", mt: 8 }}>
            <ReceiptLongRoundedIcon
              sx={{ fontSize: 52, color: "#9EB8C4", mb: 1.5 }}
            />
            <Typography
              sx={{ fontSize: 22, fontWeight: 800, color: "#17364a" }}
            >
              No orders to track
            </Typography>
            <Typography sx={{ mt: 1, color: "#577383", fontSize: 15 }}>
              New orders stay here until they are completed or older than 3
              hours.
            </Typography>
            <Button
              variant="contained"
              onClick={goToMenu}
              sx={{
                mt: 3,
                minHeight: 48,
                px: 3,
                borderRadius: "14px",
                textTransform: "none",
                fontWeight: 800,
                backgroundColor: "#003049",
              }}
            >
              Back to menu
            </Button>
          </Box>
        ) : (
          orders.map((entry, index) => {
            const displayStatus = getTrackedOrderDisplayStatus(entry);

            return (
            <motion.div
              key={entry.razorpayOrderId}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.04 }}
            >
              <Box
                onClick={() =>
                  navigate(`/${outletId}/order-status`, {
                    state: {
                      razorpayOrderId: entry.razorpayOrderId,
                    },
                  })
                }
                sx={{
                  mb: 1.2,
                  p: 1.6,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  borderRadius: "18px",
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #D5EAF3",
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: displayStatus.isCancelled
                      ? "#FDECEC"
                      : displayStatus.isReady
                        ? "#E8F8EF"
                        : "#E7F6FE",
                    color: displayStatus.isCancelled
                      ? "#D94848"
                      : displayStatus.isReady
                        ? "#1D9E59"
                        : "#09A5F7",
                    flexShrink: 0,
                  }}
                >
                  <ReceiptLongRoundedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 800,
                      color: "#0B4A67",
                    }}
                  >
                    {entry.orderNumber
                      ? `Order #${entry.orderNumber}`
                      : "Order placed"}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.25,
                      fontSize: 12,
                      fontWeight: 600,
                      color: displayStatus.isCancelled
                        ? "#D94848"
                        : displayStatus.isReady
                          ? "#1D9E59"
                          : "#668594",
                    }}
                  >
                    {displayStatus.label}
                    {entry.placedAt && (
                      <Box
                        component="span"
                        sx={{
                          textTransform: "uppercase",
                          color: "#668594",
                        }}
                      >
                        {" · "}
                        {formatPlacedAt(entry.placedAt)}
                      </Box>
                    )}
                  </Typography>
                </Box>
                <ChevronRightRoundedIcon sx={{ color: "#7A93A0" }} />
              </Box>
            </motion.div>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default OrdersList;
